import * as ChatInputActions from '@shared/scenes/chat/input/store/actions';
import * as MBMessagePersist from '@shared/scenes/chat/thread/util/persist';
import { messageQueue } from '@shared/scenes/chat/thread/util/queue';
import { getStore } from '@shared/store';
import { API } from '@shared/types/api';
import { MBAnalytics } from '@shared/util/analytics';
import { endEditingMessage } from './messageDraft';
import { messageFromDraft } from './messageFromDraft';

/*
    Send a message from a message draft. This is currently used by native to
    start sending a message.
*/
export function sendMessageFromDraft(draft: MBChatMessageDraft, channelSlug: string) {
  return async (dispatch: MBDispatch) => {
    try {
      dispatch(ChatInputActions.hideTooltips());
      const message = messageFromDraft(draft);
      await messageQueue.add(message, channelSlug);
    } catch (e) {
      // console.warn('failed sending message:', e.message);
    }
  };
}

/*
    Try to send messages in a particular channel
*/
export function resend(channelSlug: string) {
  return async (_unused: MBDispatch) => {
    try {
      await messageQueue.retry(channelSlug);
    } catch (e) {
      // console.warn('failed resending message:', e.message);
    }
  };
}

/*
    Restores the data saved in setupListeningToChatMessagesToPersist
    from AsyncStorage to the redux store.

    Note: we don't restore message attachments and if they've been uploaded.
    The reason is that we know which attachments have been uploaded by
    looking if their url is public (http) or local.
*/
export async function restoreFromPersist() {
  const store = getStore();
  const restored = await MBMessagePersist.restore();
  if (!restored) {
    return null;
  }
  store.dispatch({ type: 'CHAT:PERSIST_MESSAGES_FROM_STORAGE', value: restored });
}

/*
    Resend all messages in all channel. This is typically done when
    reconnecting or after restoring all messages on app start.
*/
export async function resendAll() {
  const store = getStore();
  const cachedMessageUuids = store.getState().chatMessages.cachedMessageUuidsFromChannelSlug;
  const channelSlugs = Object.keys(cachedMessageUuids).filter(
    slug => cachedMessageUuids[slug].length > 0,
  );
  await Promise.all(channelSlugs.map(slug => messageQueue.retry(slug)));
}

export function deleteMessage(message: API.ChatMessageFragment, channelSlug: string) {
  return async (_unused: MBDispatch) => {
    try {
      await messageQueue.delete(message, channelSlug);
      MBAnalytics.track('Message::Delete', { messageId: message.id });
    } catch (err) {
      // TODO(fant): how do we notify the user?
      if (err instanceof Error) {
        console.warn('failed to delete message:', err.message);
      }
    }
  };
}

export function editMessage({
  message,
  content,
  channelSlug,
}: {
  message: API.ChatMessageFragment;
  content: string;
  channelSlug: string;
}) {
  return async (dispatch: MBDispatch) => {
    endEditingMessage(channelSlug)(dispatch);
    try {
      await messageQueue.performEditAPIRequest(message, content, channelSlug);
      MBAnalytics.track('Message::Edit', { messageId: message.id });
    } catch (err) {
      // TODO(fant): how do we notify the user?
      if (err instanceof Error) {
        console.warn('failed to edit message:', err.message);
      }
    }
  };
}
